import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    IconButton,
    Tooltip,
    Stack,
    TextField,
    MenuItem,
    Snackbar,
    Alert,
    Divider,
    Grid,
    TablePagination,
    Autocomplete,
    FormControlLabel,
    Checkbox
} from "@mui/material";
import {
    History as HistoryIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Refresh as RefreshIcon,
    Save as SaveIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Block as CancelIcon,
    Visibility as ViewIcon,
    FileDownload as ExportIcon
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import AppHeader from "../../components/AppHeader";
import { buildHierarchicalList } from "../../components/staff/constants";
import { formatDDMMYYYY, formatYangon, formatYYYYMMDD } from "../../services/datetimeService";

const CURRENT_YEAR = new Date().getFullYear();

const LeaveManagement = ({ setIsLoggedIn }) => {
    // List state
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [staffs, setStaffs] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [availableYears, setAvailableYears] = useState([CURRENT_YEAR + 1, CURRENT_YEAR]);
    const [departments, setDepartments] = useState([]);
    const [branches, setBranches] = useState([]);

    // Pagination & Filter State
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [totalRows, setTotalRows] = useState(0);
    const [filters, setFilters] = useState({
        staff_code: "",
        staff_name: "",
        status: "",
        leave_type_id: "",
        date_from: "",
        date_to: "",
        org_node_id: "",
        branch_id: "",
        year: new Date().getFullYear().toString()
    });

    // History dialog state
    const [historyOpen, setHistoryOpen] = useState(false);
    const [history, setHistory] = useState([]);
    const [selectedRequest, setSelectedRequest] = useState(null);

    // Form dialog state (Add/Edit)
    const [formOpen, setFormOpen] = useState(false);
    const [editingRequest, setEditingRequest] = useState(null);
    const [formData, setFormData] = useState({
        staff_id: "",
        leave_type_id: "",
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        half_day_type: "none",
        start_day_type: "full",
        end_day_type: "full",
        reason: "",
        attachment_path: "",
        exclude_sandwich: false
    });

    const [uploadingDoc, setUploadingDoc] = useState(false);

    const [formLoading, setFormLoading] = useState(false);
    const [calcLoading, setCalcLoading] = useState(false);
    const [calculatedDays, setCalculatedDays] = useState(null);
    const [feedback, setFeedback] = useState({ open: false, message: "", severity: "success" });

    // Photo preview state
    const [photoOpen, setPhotoOpen] = useState(false);
    const [photoUrl, setPhotoUrl] = useState("");
    const [photoLoading, setPhotoLoading] = useState(false);
    const [photoTitle, setPhotoTitle] = useState("");

    const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
    const lookupsLoaded = useRef(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                pageSize,
                ...filters,
                year: (filters.date_from || filters.date_to) ? "" : filters.year
            };
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/leave/admin/list`, {
                params,
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });
            if (res.data.success) {
                setItems(res.data.data || []);
                setTotalRows(res.data.pagination?.total || 0);
                const years = new Set([
                    CURRENT_YEAR + 1,
                    CURRENT_YEAR,
                    ...(res.data.availableYears || []).map(Number)
                ]);
                setAvailableYears([...years].filter(Number.isInteger).sort((a, b) => b - a));
            }
        } catch (err) {
            console.error("Load data error:", err);
        }
        setLoading(false);
    }, [tenant, page, pageSize, filters]);

    const loadLookups = useCallback(async () => {
        try {
            const [staffResult, typeResult, branchResult, nodeResult] = await Promise.allSettled([
                axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff`, { withCredentials: true, headers: { "X-Tenant": tenant } }),
                axios.get(`${process.env.REACT_APP_API_URL1}/leave/types`, { withCredentials: true, headers: { "X-Tenant": tenant } }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, { withCredentials: true, headers: { "X-Tenant": tenant } }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, { withCredentials: true, headers: { "X-Tenant": tenant } })
            ]);

            const staffRes = staffResult.status === "fulfilled" ? staffResult.value : null;
            const typeRes = typeResult.status === "fulfilled" ? typeResult.value : null;
            const branchRes = branchResult.status === "fulfilled" ? branchResult.value : null;
            const nodeRes = nodeResult.status === "fulfilled" ? nodeResult.value : null;

            const rawStaffList = Array.isArray(staffRes?.data)
                ? staffRes.data
                : (staffRes?.data?.staff || staffRes?.data?.rows || []);

            // Deduplicate by staff_id (safety net for any duplicated rows from DB)
            const seen = new Set();
            const staffList = rawStaffList.filter(s => {
                const key = String(s.staff_id);
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });

            setStaffs(staffList);
            if (typeRes?.data?.success) setLeaveTypes(typeRes.data.data || []);
            if (branchRes?.data?.success) setBranches(branchRes.data.data || []);
            if (nodeRes?.data?.success) setDepartments(buildHierarchicalList(nodeRes.data.data || []));
        } catch (err) {
            console.error("Load lookups error:", err);
        }
    }, [tenant]);

    // Load data re-runs on page/filter/pagination changes
    useEffect(() => {
        loadData();
    }, [loadData]);

    // Load lookups ONCE on mount — ref guard prevents re-runs caused by re-renders
    useEffect(() => {
        if (lookupsLoaded.current) return;
        lookupsLoaded.current = true;
        loadLookups();
    }, [loadLookups]);

    useEffect(() => {
        const fetchPreview = async () => {
            if (!formData.staff_id || !formData.leave_type_id || !formData.start_date || !formData.end_date) {
                setCalculatedDays(null);
                return;
            }
            setCalcLoading(true);
            try {
                const res = await axios.post(`${process.env.REACT_APP_API_URL1}/leave/calculate-days`, formData, {
                    withCredentials: true,
                    headers: { "X-Tenant": tenant }
                });
                if (res.data.success) {
                    setCalculatedDays(res.data.totalDays);
                }
            } catch (err) {
                console.error("Preview calculation error:", err);
            }
            setCalcLoading(false);
        };

        const timer = setTimeout(fetchPreview, 500);
        return () => clearTimeout(timer);
    }, [formData.staff_id, formData.leave_type_id, formData.start_date, formData.end_date, formData.half_day_type, formData.start_day_type, formData.end_day_type, formData.exclude_sandwich, tenant]);

    const viewHistory = async (request) => {
        setSelectedRequest(request);
        setHistoryOpen(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/leave/admin/history/${request.id}`, {
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });
            if (res.data.success) {
                setHistory(res.data.data);
            }
        } catch (err) {
            console.error("Load history error:", err);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingDoc(true);
        try {
            const token = localStorage.getItem("token");
            const fd = new FormData();
            fd.append('file', file);
            fd.append('module', 'leave');

            const res = await axios.post(`${process.env.REACT_APP_API_URL1}/admin/documents`, fd, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'X-Tenant': tenant,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (res.data.success) {
                setFormData(prev => ({ ...prev, attachment_path: res.data.dms_id }));
                setFeedback({ open: true, message: 'File uploaded successfully!', severity: 'success' });
            }
        } catch (err) {
            console.error('Upload failed', err);
            setFeedback({ open: true, message: 'Upload failed. Please try again.', severity: 'error' });
        } finally {
            setUploadingDoc(false);
        }
    };

    const viewPhoto = async (request) => {
        if (!request.attachment_path) return;
        setPhotoTitle(`${request.full_name} - Supporting Document`);
        setPhotoOpen(true);
        setPhotoLoading(true);
        setPhotoUrl("");

        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL1}/admin/documents/${request.attachment_path}/preview`,
                {
                    withCredentials: true,
                    headers: {
                        "X-Tenant": tenant,
                        "Authorization": `Bearer ${token}`
                    },
                    responseType: 'blob'
                }
            );

            const contentType = res.headers['content-type'];
            const blob = new Blob([res.data], { type: contentType });
            const objectUrl = URL.createObjectURL(blob);
            setPhotoUrl(objectUrl);
        } catch (err) {
            console.error("View photo error:", err);
            setFeedback({ open: true, message: "Failed to load document", severity: "error" });
        } finally {
            setPhotoLoading(false);
        }
    };

    const handleClosePhoto = () => {
        if (photoUrl && photoUrl.startsWith('blob:')) {
            URL.revokeObjectURL(photoUrl);
        }
        setPhotoOpen(false);
        setPhotoUrl("");
    };

    const handleAdminAction = async (id, action) => {
        const confirmMsg = action === 'cancel'
            ? "Are you sure you want to cancel this approved leave? The balance will be reverted."
            : `Are you sure you want to ${action} this leave request?`;

        if (!window.confirm(confirmMsg)) return;

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL1}/leave/admin/handle-action-direct`, {
                request_id: id,
                action: action,
                comment: `By Admin`
            }, {
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });

            if (res.data.success) {
                setFeedback({ open: true, message: `Request ${action}ed`, severity: "success" });
                loadData();
            } else {
                setFeedback({ open: true, message: res.data.message || "Action failed", severity: "error" });
            }
        } catch (err) {
            setFeedback({ open: true, message: "Server error", severity: "error" });
        }
    };

    const handleAddClick = () => {
        setEditingRequest(null);
        setFormData({
            staff_id: "",
            leave_type_id: "",
            start_date: new Date().toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0],
            half_day_type: "none",
            start_day_type: "full",
            end_day_type: "full",
            reason: "",
            attachment_path: ""
        });
        setFormOpen(true);
    };

    const updateFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPage(0);
    };

    const resetFilters = () => {
        setFilters({
            staff_code: "",
            staff_name: "",
            status: "",
            leave_type_id: "",
            date_from: "",
            date_to: "",
            org_node_id: "",
            branch_id: "",
            year: new Date().getFullYear().toString()
        });
        setPage(0);
    };

    const handleEditClick = (req) => {
        setEditingRequest(req);
        setFormData({
            staff_id: req.staff_id,
            leave_type_id: req.leave_type_id,
            start_date: formatYYYYMMDD(req.start_date),
            end_date: formatYYYYMMDD(req.end_date),
            half_day_type: req.half_day_type || "none",
            start_day_type: req.start_day_type || "full",
            end_day_type: req.end_day_type || "full",
            reason: req.reason || "",
            attachment_path: req.attachment_path || ""
        });
        setFormOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this leave record? This will revert any deducted balance.")) return;
        try {
            const res = await axios.delete(`${process.env.REACT_APP_API_URL1}/leave/admin/request/${id}`, {
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });
            if (res.data.success) {
                setFeedback({ open: true, message: "Record deleted", severity: "success" });
                loadData();
            } else {
                setFeedback({ open: true, message: res.data.message || "Delete failed", severity: "error" });
            }
        } catch (err) {
            setFeedback({ open: true, message: "Server error", severity: "error" });
        }
    };

    const handleFormSubmit = async () => {
        if (!formData.staff_id || !formData.leave_type_id || !formData.start_date || !formData.end_date) {
            setFeedback({ open: true, message: "Missing required fields", severity: "warning" });
            return;
        }

        setFormLoading(true);
        try {
            const url = editingRequest
                ? `${process.env.REACT_APP_API_URL1}/leave/admin/request/${editingRequest.id}`
                : `${process.env.REACT_APP_API_URL1}/leave/admin/submit-record`;

            const method = editingRequest ? "put" : "post";

            const res = await axios[method](url, formData, {
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });

            if (res.data.success) {
                setFeedback({ open: true, message: editingRequest ? "Record updated" : "Record added", severity: "success" });
                setFormOpen(false);
                loadData();
            } else {
                setFeedback({ open: true, message: res.data.message || "Action failed", severity: "error" });
            }
        } catch (err) {
            setFeedback({ open: true, message: err.response?.data?.message || "Server error", severity: "error" });
        }
        setFormLoading(false);
    };

    const selectedLeaveType = leaveTypes.find(t => t.id === formData.leave_type_id);
    const isHalfDayAllowed = selectedLeaveType ? selectedLeaveType.allow_half_day !== 0 : true;

    const [exportLoading, setExportLoading] = useState(false);

    const handleExportExcel = async () => {
        try {
            setExportLoading(true);
            const params = {
                page: 1,
                pageSize: 10000,
                ...filters,
                year: (filters.date_from || filters.date_to) ? "" : filters.year
            };
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/leave/admin/list`, {
                params,
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });

            const exportData = res.data?.data || items;
            if (!exportData || exportData.length === 0) {
                setFeedback({ open: true, message: "No records to export", severity: "warning" });
                return;
            }

            const wb = XLSX.utils.book_new();
            const headers = [
                "No.",
                "Employee ID",
                "Staff Code",
                "Staff Name",
                "Department",
                "Branch",
                "Leave Type",
                "Start Date",
                "End Date",
                "Day Portion",
                "Total Days",
                "Status",
                "Reason",
                "Applied Date"
            ];

            const sheetData = [headers];
            exportData.forEach((row, index) => {
                let portion = "Full Day";
                if (row.start_date === row.end_date) {
                    if (row.half_day_type === 'morning') portion = "Morning Half";
                    else if (row.half_day_type === 'afternoon') portion = "Afternoon Half";
                } else {
                    const parts = [];
                    if (row.start_day_type === 'afternoon') parts.push("Start PM");
                    if (row.end_day_type === 'morning') parts.push("End AM");
                    if (parts.length > 0) portion = parts.join(", ");
                }

                sheetData.push([
                    index + 1,
                    row.employee_number || "",
                    row.staff_code || "",
                    row.full_name || "",
                    row.department || "",
                    row.branch_name || "",
                    row.type_name || row.type_code || "",
                    formatDDMMYYYY(row.start_date),
                    formatDDMMYYYY(row.end_date),
                    portion,
                    parseFloat(row.total_days) || 0,
                    (row.status || "").toUpperCase(),
                    row.reason || "",
                    row.created_at ? formatDDMMYYYY(row.created_at) : ""
                ]);
            });

            const ws = XLSX.utils.aoa_to_sheet(sheetData);
            ws['!cols'] = [
                { wch: 6 },
                { wch: 15 },
                { wch: 15 },
                { wch: 25 },
                { wch: 20 },
                { wch: 20 },
                { wch: 18 },
                { wch: 14 },
                { wch: 14 },
                { wch: 15 },
                { wch: 12 },
                { wch: 14 },
                { wch: 30 },
                { wch: 14 }
            ];

            XLSX.utils.book_append_sheet(wb, ws, "Leave Records");
            const dateStr = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Leave_Records_${dateStr}.xlsx`);
        } catch (err) {
            console.error("Export leave records error:", err);
            setFeedback({ open: true, message: "Export failed: " + (err.message || "Unknown error"), severity: "error" });
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa" }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ p: { xs: 2, md: 4 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h4" fontWeight="bold">Leave Management</Typography>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={loadData}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="outlined"
                            color="success"
                            startIcon={exportLoading ? <CircularProgress size={16} /> : <ExportIcon />}
                            onClick={handleExportExcel}
                            disabled={exportLoading || items.length === 0}
                        >
                            {exportLoading ? "Exporting..." : "Export Excel"}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddClick}
                        >
                            Add Record
                        </Button>
                    </Stack>
                </Stack>

                <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                    <Grid
                        container
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1.2fr) repeat(3, minmax(0, 1fr)) minmax(0, 1.1fr) minmax(0, 0.65fr) repeat(2, minmax(0, 1.2fr)) repeat(2, minmax(0, 0.65fr))",
                            gap: 1.5,
                            alignItems: "start",
                            width: "100%"
                        }}
                    >
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                label="Staff Code / Employee No."
                                size="small"
                                value={filters.staff_code}
                                onChange={(e) => updateFilter("staff_code", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                label="Staff Name"
                                size="small"
                                value={filters.staff_name}
                                onChange={(e) => updateFilter("staff_name", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                select
                                fullWidth
                                label="Leave Type"
                                size="small"
                                value={filters.leave_type_id}
                                onChange={(e) => updateFilter("leave_type_id", e.target.value)}
                            >
                                <MenuItem value="">All Types</MenuItem>
                                {leaveTypes.map((type) => (
                                    <MenuItem key={type.id} value={type.id}>
                                        {type.type_name} ({type.type_code})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                select
                                fullWidth
                                label="Branch"
                                size="small"
                                value={filters.branch_id}
                                onChange={(e) => updateFilter("branch_id", e.target.value)}
                            >
                                <MenuItem value="">All Branches</MenuItem>
                                {branches.map((branch) => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.branch_name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                select
                                fullWidth
                                label="Status"
                                size="small"
                                value={filters.status}
                                onChange={(e) => updateFilter("status", e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="submitted">Submitted</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="in_review">In Review</MenuItem>
                                <MenuItem value="approved">Approved</MenuItem>
                                <MenuItem value="rejected">Rejected</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                select
                                fullWidth
                                label="Department"
                                size="small"
                                value={filters.org_node_id}
                                onChange={(e) => updateFilter("org_node_id", e.target.value)}
                            >
                                <MenuItem value="">All Departments</MenuItem>
                                {departments.map((department) => (
                                    <MenuItem key={department.id} value={department.id} sx={{ pl: 2 + (department._depth || 0) * 2 }}>
                                        {department._depth > 0 && <span style={{ color: '#94a3b8', marginRight: 4 }}>└</span>}
                                        {department.node_name}{department.level_type ? ` (${department.level_type})` : ''}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                select
                                fullWidth
                                label={(filters.date_from || filters.date_to) ? "Year (ignored with dates)" : "Year"}
                                size="small"
                                value={filters.year}
                                onChange={(e) => updateFilter("year", e.target.value)}
                                disabled={!!(filters.date_from || filters.date_to)}
                            >
                                <MenuItem value="">All Years</MenuItem>
                                {availableYears.map(y => (
                                    <MenuItem key={y} value={y.toString()}>{y}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                label="Start Date"
                                size="small"
                                type="date"
                                value={filters.date_from}
                                onChange={(e) => updateFilter("date_from", e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                helperText="Overlap from"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                label="End Date"
                                size="small"
                                type="date"
                                value={filters.date_to}
                                onChange={(e) => updateFilter("date_to", e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                helperText="Overlap to"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Button variant="contained" fullWidth onClick={() => { setPage(0); loadData(); }}>
                                Filter
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Button variant="outlined" fullWidth onClick={resetFilters}>
                                Reset
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                <Paper sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", overflow: 'hidden' }}>
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}><CircularProgress /></Box>
                    ) : (
                        <Table size="small">
                            <TableHead sx={{ bgcolor: "#fafafa" }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Staff</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Dates</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Days</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.length === 0 ? (
                                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}>No leave records found.</TableCell></TableRow>
                                ) : (
                                    items.map((row) => (
                                        <TableRow key={row.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold">{row.full_name}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {[row.employee_number, row.staff_code].filter(Boolean).join(" · ")}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{row.type_code}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{formatDDMMYYYY(row.start_date)}</Typography>
                                                <Typography variant="caption" color="textSecondary">to {formatDDMMYYYY(row.end_date)}</Typography>
                                                {row.start_date === row.end_date ? (
                                                    row.half_day_type !== 'none' && <Chip label={row.half_day_type} size="small" variant="outlined" sx={{ ml: 1, height: 20, fontSize: 10, textTransform: 'capitalize' }} />
                                                ) : (
                                                    <>
                                                        {row.start_day_type === 'afternoon' && <Chip label="Start PM" size="small" variant="outlined" sx={{ ml: 1, height: 20, fontSize: 10 }} />}
                                                        {row.end_day_type === 'morning' && <Chip label="End AM" size="small" variant="outlined" sx={{ ml: 1, height: 20, fontSize: 10 }} />}
                                                    </>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">{row.total_days}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={row.status}
                                                    color={row.status === "approved" ? "success" : row.status === "rejected" ? "error" : "warning"}
                                                    variant="soft"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {row.reason}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    {row.status !== 'approved' && row.status !== 'cancelled' && (
                                                        <Tooltip title="Approve">
                                                            <IconButton size="small" color="success" onClick={() => handleAdminAction(row.id, 'approve')}>
                                                                <ApproveIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {(row.status === 'pending' || row.status === 'in_review' || row.status === 'submitted') && (
                                                        <Tooltip title="Reject">
                                                            <IconButton size="small" color="error" onClick={() => handleAdminAction(row.id, 'reject')}>
                                                                <RejectIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {row.status === 'approved' && (
                                                        <Tooltip title="Cancel (Approved)">
                                                            <IconButton size="small" color="warning" onClick={() => handleAdminAction(row.id, 'cancel')}>
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                                                    {row.attachment_path && (
                                                        <Tooltip title="View Attachment">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => viewPhoto(row)}
                                                            >
                                                                <ViewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    <Tooltip title="View History">
                                                        <IconButton size="small" onClick={() => viewHistory(row)}>
                                                            <HistoryIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" color="primary" onClick={() => handleEditClick(row)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                    <TablePagination
                        component="div"
                        count={totalRows}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={(e) => {
                            setPageSize(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            </Box>

            {/* Entry/Edit Form Dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>{editingRequest ? "Edit Leave Record" : "Add Leave Record"}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <Autocomplete
                            fullWidth
                            options={staffs}
                            getOptionLabel={(option) => `${option.full_name} (${option.staff_code})`}
                            isOptionEqualToValue={(option, value) => String(option.staff_id) === String(value?.staff_id)}
                            renderOption={(props, option) => (
                                <li {...props} key={String(option.staff_id)}>
                                    {option.full_name} ({option.staff_code})
                                </li>
                            )}
                            value={staffs.find(s => String(s.staff_id) === String(formData.staff_id)) || null}
                            onChange={(event, newValue) => {
                                setFormData({ ...formData, staff_id: newValue ? newValue.staff_id : "" });
                            }}
                            disabled={!!editingRequest}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Staff Member"
                                    size="small"
                                    required
                                />
                            )}
                        />

                        <TextField
                            select
                            fullWidth
                            label="Leave Type"
                            size="small"
                            value={formData.leave_type_id}
                            onChange={(e) => {
                                const newTypeId = e.target.value;
                                const t = leaveTypes.find(x => x.id === newTypeId);
                                const allowed = t ? t.allow_half_day !== 0 : true;
                                setFormData({
                                    ...formData,
                                    leave_type_id: newTypeId,
                                    ...(allowed ? {} : { half_day_type: 'none', start_day_type: 'full', end_day_type: 'full' })
                                });
                            }}
                        >
                            {leaveTypes.map((t) => (
                                <MenuItem key={t.id} value={t.id}>
                                    {t.type_name} ({t.type_code})
                                </MenuItem>
                            ))}
                        </TextField>

                        <Stack direction="row" spacing={2}>
                            <TextField
                                fullWidth
                                label="Start Date"
                                size="small"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={formData.start_date}
                                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                            />
                            <TextField
                                fullWidth
                                label="End Date"
                                size="small"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={formData.end_date}
                                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                            />
                        </Stack>

                        {isHalfDayAllowed && (
                            formData.start_date === formData.end_date ? (
                                <TextField
                                    select
                                    fullWidth
                                    label="Half Day"
                                    size="small"
                                    value={formData.half_day_type}
                                    onChange={(e) => setFormData({ ...formData, half_day_type: e.target.value })}
                                >
                                    <MenuItem value="none">Full Day</MenuItem>
                                    <MenuItem value="morning">Morning Half</MenuItem>
                                    <MenuItem value="afternoon">Afternoon Half</MenuItem>
                                </TextField>
                            ) : (
                                <Stack direction="row" spacing={2}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Start Day"
                                        size="small"
                                        value={formData.start_day_type}
                                        onChange={(e) => setFormData({ ...formData, start_day_type: e.target.value })}
                                    >
                                        <MenuItem value="full">Full Day</MenuItem>
                                        <MenuItem value="afternoon">Afternoon Half</MenuItem>
                                    </TextField>
                                    <TextField
                                        select
                                        fullWidth
                                        label="End Day"
                                        size="small"
                                        value={formData.end_day_type}
                                        onChange={(e) => setFormData({ ...formData, end_day_type: e.target.value })}
                                    >
                                        <MenuItem value="full">Full Day</MenuItem>
                                        <MenuItem value="morning">Morning Half</MenuItem>
                                    </TextField>
                                </Stack>
                            )
                        )}

                        <TextField
                            fullWidth
                            label="Reason"
                            size="small"
                            multiline
                            rows={3}
                            value={formData.reason}
                            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(formData.exclude_sandwich)}
                                    onChange={(e) => setFormData({ ...formData, exclude_sandwich: e.target.checked })}
                                    color="primary"
                                />
                            }
                            label={<Typography variant="body2" fontWeight="bold" color="primary.main">Exclude Sandwich Rule (HR Override)</Typography>}
                        />

                        <Box>
                            <Typography variant="body2" color="textSecondary" mb={1}>Supporting Document (Optional/Required based on Policy)</Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button
                                    variant="outlined"
                                    component="label"
                                    disabled={uploadingDoc}
                                    startIcon={uploadingDoc ? <CircularProgress size={20} /> : <SaveIcon />}
                                >
                                    {uploadingDoc ? 'Uploading...' : formData.attachment_path ? 'Change File' : 'Upload File'}
                                    <input type="file" hidden onChange={handleFileUpload} />
                                </Button>
                                {formData.attachment_path && (
                                    <Typography variant="body2" color="success.main">
                                        ✓ File attached
                                    </Typography>
                                )}
                            </Stack>
                        </Box>

                        {calculatedDays !== null && (
                            <Box sx={{
                                p: 2,
                                bgcolor: calculatedDays === 0 ? "rgba(211, 47, 47, 0.08)" : "rgba(25, 118, 210, 0.08)",
                                borderRadius: 1,
                                border: '1px dashed',
                                borderColor: calculatedDays === 0 ? '#d32f2f' : '#1976d2'
                            }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2" color={calculatedDays === 0 ? "error.main" : "primary.main"} fontWeight="bold">
                                        {calculatedDays === 0 ? "Non-Working Day:" : "Total Deduction:"}
                                    </Typography>
                                    <Typography variant="h6" color={calculatedDays === 0 ? "error.main" : "primary.main"} fontWeight="bold">
                                        {calcLoading ? "..." : `${calculatedDays} Days`}
                                    </Typography>
                                </Stack>
                                <Typography variant="caption" color="textSecondary">
                                    {calculatedDays === 0
                                        ? "* This date is a REST day or Holiday for this staff. No leave is needed."
                                        : "* Respects Roster, Holidays, and Sandwich Rule."}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setFormOpen(false)} color="inherit">Cancel</Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleFormSubmit}
                        disabled={formLoading}
                    >
                        {editingRequest ? "Update Record" : "Save Record"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* History Dialog */}
            <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Approval History - {selectedRequest?.full_name}</DialogTitle>
                <DialogContent dividers>
                    {history.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">No history recorded.</Typography>
                    ) : (
                        <Stack spacing={2}>
                            {history.map((h, index) => (
                                <Box key={index} sx={{ p: 2, bgcolor: "#f8f9fa", borderRadius: 1, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                            {h.action?.toUpperCase()}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {formatYangon(h.acted_at)}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        By: <strong>{h.acted_by_name || "System"}</strong>
                                    </Typography>
                                    {h.comment && (
                                        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary", bgcolor: "white", p: 1, borderRadius: 0.5 }}>
                                            "{h.comment}"
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Photo Preview Dialog */}
            <Dialog open={photoOpen} onClose={handleClosePhoto} maxWidth="md" fullWidth>
                <DialogTitle>{photoTitle}</DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                    {photoLoading ? (
                        <CircularProgress />
                    ) : photoUrl ? (
                        <Box
                            component="img"
                            src={photoUrl}
                            alt="Attachment"
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '70vh',
                                objectFit: 'contain',
                                borderRadius: 1,
                                boxShadow: 3
                            }}
                        />
                    ) : (
                        <Typography color="textSecondary">Document not found or failed to load.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {photoUrl && (
                        <Button color="primary" onClick={() => window.open(photoUrl, '_blank')}>
                            Open in New Tab
                        </Button>
                    )}
                    <Button onClick={handleClosePhoto}>Close</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={feedback.open}
                autoHideDuration={4000}
                onClose={() => setFeedback({ ...feedback, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setFeedback({ ...feedback, open: false })} severity={feedback.severity} sx={{ width: '100%' }}>
                    {feedback.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LeaveManagement;
